/* eslint-disable react/destructuring-assignment */
import React, {
  Dispatch, SetStateAction,
} from 'react'
import {
  Controller, FieldValues, Path, useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ISelectOption } from 'types/IClassifier'
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters'
import AsyncSelect from 'react-select/async'
import { filterCustomsOfficeOptions, getSelectValue } from 'helpers'
import { DEFAULT_FIELD_RULE, FieldRule } from '../../features/declaration/models/fieldRules'

export type SelectProps<TFieldValues extends FieldValues> = {
  field: Path<TFieldValues>
  labelKey: string
  isClearable?: boolean
  isDisabled?: boolean
  isInvalid?: boolean
  customFilterOption?: (option: FilterOptionOption<ISelectOption>, rawInput: string) => boolean
  fieldRule?: FieldRule
  emptyValue?: string | number | null
} & ({
  type: 'async'
  loadOptions: (inputValue: string) => Promise<ISelectOption[]>
  valueBuffer: ISelectOption | null
  setValueBuffer: Dispatch<SetStateAction<ISelectOption | null>>
} | {
  type: 'sync'
  options: ISelectOption[]
})

function appendFilter(customFilterOption: ((option: FilterOptionOption<ISelectOption>, rawInput: string) => boolean) | undefined) {
  if (customFilterOption === null || customFilterOption === undefined) return {}
  return { filterOption: customFilterOption }
}

function FormSelect<TFieldValues extends FieldValues>(selectProps: SelectProps<TFieldValues>) {
  const {
    field,
    labelKey,
    isClearable,
    isDisabled,
    fieldRule = DEFAULT_FIELD_RULE,
    customFilterOption,
    emptyValue = '',
  } = selectProps

  const { control } = useFormContext<TFieldValues>()
  const { t } = useTranslation()

  const fallbackForBigOptionsArray = (
    inputValue: string,
    loadingCallback: (func: any) => void,
  ) => {
    if (selectProps.type === 'sync') {
      loadingCallback(filterCustomsOfficeOptions(inputValue, selectProps.options, ' - '))
    }
  }

  return (
    <Controller
      control={control}
      name={field}
      render={({ field: { onChange, value, ref }, fieldState }) => {
        if (selectProps.type === 'async') {
          return (
            <AsyncSelect
              ref={ref}
              key={`${field}_${value}${selectProps.valueBuffer?.label}`}
              isDisabled={isDisabled || fieldRule.requirement === 'forbidden'}
              isClearable={isClearable}
              className={`select menu-list--auto ${fieldState.invalid ? 'form-control p-0 pe-3 is-invalid' : ''} ${
                fieldRule.requirement === 'forbidden' ? 'cursor--not-allowed' : ''
              }`}
              defaultOptions={selectProps.valueBuffer ? [selectProps.valueBuffer] : []}
              value={selectProps.valueBuffer}
              menuPlacement="auto"
              classNamePrefix="select"
              loadOptions={selectProps.loadOptions}
              placeholder={t(labelKey)}
              onChange={(option: ISelectOption | null) => {
                onChange(option?.value ?? emptyValue)
                selectProps.setValueBuffer(option)
              }}
              {...(appendFilter(customFilterOption))}
            />
          )
        }

        // eslint-disable-next-line react/destructuring-assignment
        const selectOptions = selectProps.options
        return (
          <AsyncSelect
            ref={ref}
            key={field + value}
            isDisabled={isDisabled || fieldRule.requirement === 'forbidden'}
            isClearable={isClearable}
            className={`bg-red select menu-list--auto ${fieldState.invalid ? 'form-control p-0 pe-3 is-invalid' : ''} ${
              fieldRule.requirement === 'forbidden' ? 'cursor--not-allowed' : ''
            }`}
            menuPlacement="auto"
            classNamePrefix="select"
            defaultOptions={[...selectOptions.slice(0, 200)]}
            loadOptions={fallbackForBigOptionsArray}
            placeholder={t(labelKey)}
            value={getSelectValue(value, selectOptions)}
            onChange={(option: ISelectOption | null) => {
              (onChange(option?.value ?? emptyValue))
            }}
            {...(appendFilter(customFilterOption))}
          />
        )
      }}
    />
  )
}
FormSelect.defaultProps = {
  isClearable: true,
  isDisabled: false,
  isInvalid: false,
  customFilterOption: null,
}

export default FormSelect

